import React, { Component } from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Content, Row, Col } from "../../components/GridComponent/AdminLte.js";
import { addUserAsset } from "../../store/actions/userActions";

/**
 * The `itam` custom-form slot: the IT Asset Management declaration body (SPEC 7.4 / 21.7).
 *
 * Moved out of the hand-coded Dashboard modal unchanged: read-only employee details, one entry
 * form that appends rows to an equipment table ("Add Equipment"), and a "Confirm" that asks the
 * Data Confirmation Statement before posting the whole list through addUserAsset
 * (POST /user/addasset). The frame owns the title and the mandatory chrome.
 *
 * - "Add Equipment" validates the entry (all fields required; the free text only for Others),
 *   appends it to the list and clears the entry; nothing is posted yet
 * - "Confirm" appears only once there is at least one row; window.confirm Cancel posts nothing
 * - addUserAsset true -> onComplete(); false (the thunk already alerted) -> the rows stay
 * - payload is {} for this key; eligibility (hire day 15+, not Client) is the engine's
 */

const CONFIRM_TEXT =
  "Data Confirmation Statement\n\nI confirm that all data provided is true and correct. I understand that any discrepancies, whether intentional or due to negligence, may result in disciplinary action and that I will be held fully accountable.";

const EQUIPMENT_TYPES = ["Desktop", "Laptop", "Keyboard", "Mouse", "Monitor", "Headset", "Webcam", "Wifi Modem", "Others"];

const EMPTY_ENTRY = { personal_equipment: "", equipment_type: "", serial_no: "", asset_tag: "", add_equipment_type: "" };

const validationSchemaITAM = Yup.object().shape({
  personal_equipment: Yup.string().required("This field is required").nullable(),
  equipment_type: Yup.string().required("This field is required").nullable(),
  add_equipment_type: Yup.string().nullable().when("equipment_type", {
    is: "Others",
    then: Yup.string().required("This field is required").nullable(),
  }),
  serial_no: Yup.string().required("This field is required").nullable(),
  asset_tag: Yup.string().required("This field is required").nullable(),
});

class ItamSlot extends Component {
  state = { equipment_list: [] };
  inFlight = false;

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  // Formik submit of the entry form: append the row and clear the fields.
  addEquipment = (values, formik) => {
    const new_equipment = {
      personal_equipment: values.personal_equipment,
      equipment_type: values.equipment_type,
      serial_no: values.serial_no,
      asset_tag: values.asset_tag,
      add_equipment_type: values.add_equipment_type,
    };
    this.setState({ equipment_list: [...this.state.equipment_list, new_equipment] });
    formik.resetForm({ values: { ...EMPTY_ENTRY } });
  };

  confirm = () => {
    if (this.inFlight || !window.confirm(CONFIRM_TEXT)) return;
    this.inFlight = true;
    Promise.resolve(this.props.addUserAsset(this.state.equipment_list)).then((ok) => {
      this.inFlight = false;
      if (ok && this.mounted) this.props.onComplete();
    });
  };

  renderRow = (equipment, index) => {
    // The select posts "1" / "2" as strings.
    let is_personal = "";
    if (String(equipment.personal_equipment) === "1") {
      is_personal = "Yes";
    } else if (String(equipment.personal_equipment) === "2") {
      is_personal = "No";
    }
    const equip_type = equipment.equipment_type === "Others"
      ? equipment.equipment_type + ": " + equipment.add_equipment_type
      : equipment.equipment_type;
    return (
      <tr key={index}>
        <td>{is_personal}</td>
        <td>{equip_type}</td>
        <td>{equipment.serial_no ?? "N/A"}</td>
        <td>{equipment.asset_tag ?? "N/A"}</td>
      </tr>
    );
  };

  renderEquipmentList = () => {
    const { equipment_list } = this.state;
    if (equipment_list.length === 0) {
      return <h3 style={{ marginTop: "50px" }}>No assets added yet</h3>;
    }
    return (
      <div>
        <table className="table table-bordered" style={{ marginTop: "50px" }}>
          <thead>
            <tr>
              <th scope="col">Personal Equipment</th>
              <th scope="col">Equipment Type</th>
              <th scope="col">Serial No</th>
              <th scope="col">Asset Tag</th>
            </tr>
          </thead>
          <tbody>{equipment_list.map(this.renderRow)}</tbody>
        </table>
        <span>
          <div style={{ float: "right" }}>
            <Button type="button" className="btn btn-primary-2" onClick={this.confirm}>
              <i className="fa  is-green fa-location-arrow" /> Confirm
            </Button>
          </div>
        </span>
      </div>
    );
  };

  render() {
    const { user } = this.props;

    return (
      <div className="itam-body">
        <Formik initialValues={{ ...EMPTY_ENTRY }} validationSchema={validationSchemaITAM} onSubmit={this.addEquipment}>
          {({ values, handleSubmit, handleChange }) => (
            <form onSubmit={handleSubmit}>
              <Content col="12">
                <Row>
                  <Col size="4">
                    <div className="form-group">
                      <label>Employee Name</label>
                      <input type="text" className="form-control" name="employee_name" value={user.first_name + " " + user.last_name} disabled />
                    </div>
                  </Col>
                  <Col size="4">
                    <div className="form-group">
                      <label>Employee Number</label>
                      <input type="text" className="form-control" name="emp_num" value={user.emp_num} disabled />
                    </div>
                  </Col>
                  <Col size="4">
                    <div className="form-group">
                      <label>Email</label>
                      <input type="text" className="form-control" name="email" value={user.email} disabled />
                    </div>
                  </Col>
                </Row><br />

                <Row>
                  <Col size="6">
                    <div className="form-group">
                      <label className="itam-required">Personal Equipment</label>
                      <select name="personal_equipment" className="form-control" value={values.personal_equipment} onChange={handleChange}>
                        <option value=""></option>
                        <option value="1">Yes</option>
                        <option value="2">No</option>
                      </select>
                      <ErrorMessage component="div" name="personal_equipment" className="input-feedback" />
                    </div>
                  </Col>
                  <Col size="6">
                    <div className="form-group">
                      <label className="itam-required">Equipment Type</label>
                      <select name="equipment_type" className="form-control" value={values.equipment_type} onChange={handleChange}>
                        <option value=""></option>
                        {EQUIPMENT_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <ErrorMessage component="div" name="equipment_type" className="input-feedback" /><br />
                      {values.equipment_type === "Others" ? (
                        <div>
                          <input name="add_equipment_type" type="text" className="form-control" onChange={handleChange} value={values.add_equipment_type} />
                          <ErrorMessage component="div" name="add_equipment_type" className="input-feedback" /><br />
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col size="6">
                    <div className="form-group">
                      <label className="itam-required">Serial No</label>
                      <input name="serial_no" type="text" className="form-control" onChange={handleChange} value={values.serial_no} placeholder='Please indicate "N/A" if not applicable' />
                      <ErrorMessage component="div" name="serial_no" className="input-feedback" />
                    </div>
                  </Col>
                  <Col size="6">
                    <div className="form-group">
                      <label className="itam-required">Asset Tag</label>
                      <input name="asset_tag" type="text" className="form-control" onChange={handleChange} value={values.asset_tag} placeholder='Please indicate "N/A" if not applicable' />
                      <ErrorMessage component="div" name="asset_tag" className="input-feedback" />
                    </div>
                  </Col>
                </Row><br />
                <span>
                  <div style={{ float: "right" }}>
                    <Button type="submit" className="btn btn-primary-2">
                      <i className="fa  is-green fa-location-arrow" /> Add Equipment
                    </Button>
                  </div>
                </span>

                {this.renderEquipmentList()}
              </Content>
            </form>
          )}
        </Formik>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    addUserAsset: (post_data) => dispatch(addUserAsset(post_data)),
  };
};

export default connect(null, mapDispatchToProps)(ItamSlot);
